"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Truck, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, ArrowRight, Maximize2, Zap, SlidersHorizontal as Sliders, Wrench, Boxes } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const permanentSpecs = [{
  id: "perm-1",
  title: "Operating floor space",
  desc: "Allocates a dedicated footprint with clear overhead safety zones and perimeter spectator clearance."
}, {
  id: "perm-2",
  title: "Site access & delivery",
  desc: "Accounts for door widths, freight elevators, and unloading routes during initial install."
}, {
  id: "perm-3",
  title: "Dedicated power supply",
  desc: "Fixed conduit line and direct disconnect switch to meet local commercial electrical code."
}, {
  id: "perm-4",
  title: "Controller console station",
  desc: "Permanent operator booth positioning with unimpeded 360-degree line of sight."
}, {
  id: "perm-5",
  title: "Routine maintenance access",
  desc: "Open perimeter around the mechanical chassis for regular gearbox and motor servicing."
}];
const mobileSpecs = [{
  id: "mob-1",
  title: "Packed transit dimensions",
  desc: "Engineered to pack into standard box trailers, toy haulers, or commercial vans."
}, {
  id: "mob-2",
  title: "Ramp & loading access",
  desc: "Rolling dolly frames and low center-of-gravity design for curbs, thresholds, and grass."
}, {
  id: "mob-3",
  title: "Heavy-duty inflatable handling",
  desc: "Reinforced vinyl mattress with rapid-deflation zippers for quick teardown and roll-up."
}, {
  id: "mob-4",
  title: "Secure mobile storage",
  desc: "Weatherproof transit covers and locking compartments for console and cables."
}, {
  id: "mob-5",
  title: "Rapid turnaround logistics",
  desc: "Standard 2-person assembly setup under 45 minutes with portable generator compatibility."
}];
export default function Comparison() {
  const shouldReduceMotion = useReducedMotion();
  const transitionEasing = [0.16, 1, 0.3, 1] as const;
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: transitionEasing,
        delay: shouldReduceMotion ? 0 : i * 0.06
      }
    })
  };
  return <section id="venuevsmobilecomparison" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: transitionEasing
      }} className="max-w-3xl">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 bg-primary" aria-hidden="true" />
            <Badge variant="outline" className="rounded-none border-border bg-muted/60 font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Equipment Planning Guide
            </Badge>
          </div>

          <h2 className="mt-4 font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Permanent Venue vs Mobile Mechanical Bull Setup
          </h2>

          <p className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
            Compare operating footprint, transit logistics, and power access before ordering commercial equipment.
          </p>
        </motion.div>

        {/* Comparison Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column: Permanent Venue */}
          <motion.div custom={0} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="flex flex-col border border-border bg-card p-6 shadow-xs sm:p-8">
            <div className="flex items-start justify-between border-b border-border pb-6">
              <div>
                <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground uppercase">
                  <span>Configuration A</span>
                  <span>/</span>
                  <span className="text-primary">Fixed Install</span>
                </div>
                <h3 className="mt-2 font-serif text-2xl font-bold uppercase tracking-wide text-card-foreground sm:text-3xl">
                  Permanent Venue
                </h3>
              </div>
              <div className="flex size-11 shrink-0 items-center justify-center border border-border bg-muted/50 text-foreground">
                <Building2 className="size-5" />
              </div>
            </div>

            <p className="mt-6 text-sm leading-relaxed text-muted-foreground sm:text-base">
              A fixed installation allows the buyer to plan around one operating area. Focus on available floor space, site access, power, controller position, storage and access for future maintenance.
            </p>

            <div className="mt-8 flex-1">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                Fixed Footprint Specifications
              </h4>
              <ul className="mt-4 divide-y divide-border border-y border-border">
                {permanentSpecs.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 py-3.5">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="font-sans text-sm font-semibold text-card-foreground">
                        {item.title}
                      </span>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="font-mono text-xs text-muted-foreground">
                Typical buyers: Bars, family entertainment centers & theme venues
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-none border-border font-mono text-xs uppercase">
                <Link to="/SetupSafetyAndMaintenancePage">
                  View Setup Specs
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Mobile Operation */}
          <motion.div custom={1} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="flex flex-col border border-border bg-card p-6 shadow-xs sm:p-8">
            <div className="flex items-start justify-between border-b border-border pb-6">
              <div>
                <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground uppercase">
                  <span>Configuration B</span>
                  <span>/</span>
                  <span className="text-primary">Portable Unit</span>
                </div>
                <h3 className="mt-2 font-serif text-2xl font-bold uppercase tracking-wide text-card-foreground sm:text-3xl">
                  Mobile Operation
                </h3>
              </div>
              <div className="flex size-11 shrink-0 items-center justify-center border border-border bg-muted/50 text-foreground">
                <Truck className="size-5" />
              </div>
            </div>

            <p className="mt-6 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Equipment that moves regularly adds transport and repeated setup considerations. Compare packed dimensions, loading access, inflatable handling, storage and how the complete system moves between locations.
            </p>

            <div className="mt-8 flex-1">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                Transit & Deployment Factors
              </h4>
              <ul className="mt-4 divide-y divide-border border-y border-border">
                {mobileSpecs.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 py-3.5">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <div>
                      <span className="font-sans text-sm font-semibold text-card-foreground">
                        {item.title}
                      </span>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="font-mono text-xs text-muted-foreground">
                Typical buyers: Independent event production & mobile entertainment firms
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-none border-border font-mono text-xs uppercase">
                <Link to="/NewMechanicalBullsPage">
                  Browse Equipment
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Industrial Full-Width Disclaimer Notice */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: transitionEasing,
        delay: 0.12
      }} className="mt-8 border border-border bg-muted/40 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center border border-border bg-background text-muted-foreground">
              <AlertCircle className="size-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                  Equipment Sales Notice
                </span>
                <span className="text-xs text-muted-foreground">—</span>
                <span className="font-mono text-xs text-muted-foreground">Direct Commercial Supply</span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Mechanical Bull Shop sells mechanical bulls and equipment. References to mobile event operators describe customers who may purchase equipment; they do not indicate a rental service offered by Mechanical Bull Shop.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}